
import { Vue, Component } from "vue-property-decorator";

declare const pendo: any;

@Component
export default class SolDialog extends Vue {
  private durationInterval: number | null = null;
  private durationTick: number = 0;

  get country(): string {
    return this.$route.params.country;
  }

  get solDialog(): HTMLDialogElement | null {
    if (!this.$refs?.solDialog) {
      return null;
    }
    return this.$refs.solDialog as InstanceType<typeof HTMLDialogElement>;
  }

  get recentCalls(): any[] {
    return this.$store.state.recentLiveCalls || [];
  }

  get liveCalls(): any[] {
    return this.recentCalls.filter((c) => {
      const state = (c.state || "").toLowerCase();
      return state === "in_progress" || state === "initiated";
    });
  }

  get recentCompletedCalls(): any[] {
    return this.recentCalls.filter((c) => {
      const state = (c.state || "").toLowerCase();
      return state !== "in_progress" && state !== "initiated";
    });
  }

  show(): void {
    this.$store.dispatch("fetchRecentLiveCalls", { country: this.country });
    if (this.solDialog) {
      this.solDialog.showModal();
      this.solDialog.onclose = () => {
        this.stopDurationTimer();
        this.$emit("close");
      };
    }
    this.startDurationTimer();
    pendo.track("sol_modal_open", {
      user: this.$auth.user.email,
      country: this.country,
    });
  }

  close(): void {
    this.stopDurationTimer();
    if (this.solDialog) {
      this.solDialog.close();
    }
  }

  private startDurationTimer(): void {
    this.durationInterval = window.setInterval(() => {
      this.durationTick++;
    }, 1000);
  }

  private stopDurationTimer(): void {
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }
  }

  callKey(call: any): string {
    const id =
      call.target_id ||
      call.targetId ||
      call.reference_id ||
      call.room_name ||
      call.conversation_id ||
      call.participant_phone;
    return `${call.person_uid || call.personUid || "person"}:${
      call.report_name || call.reportName || "report"
    }:${id || "default"}`;
  }

  shortReport(reportName: string): string {
    if (!reportName) return "RC";
    const parts = reportName.split("_");
    if (parts.length > 1) {
      return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
    }
    return reportName.slice(0, 2).toUpperCase();
  }

  callLabel(call: any): string {
    const report = call.report_name || call.reportName;
    if (report === "candidate_screening") {
      return "Candidate Screening";
    }
    if (report === "reference_check") {
      return "Reference Check";
    }
    return report?.replace(/_/g, " ") || "Call";
  }

  formatState(state: string): string {
    const s = (state || "").toLowerCase();
    if (s === "in_progress") return "In Progress";
    if (s === "initiated") return "Connecting";
    if (s === "completed") return "Completed";
    if (s === "failed") return "Failed";
    if (s === "declined") return "Declined";
    return state;
  }

  statusClass(state: string): string {
    const s = (state || "").toLowerCase();
    if (s === "completed") return "status-completed";
    if (s === "failed" || s === "declined") return "status-failed";
    if (s === "initiated") return "status-initiated";
    return "status-live";
  }

  avatarClass(call: any): string {
    const s = (call.state || "").toLowerCase();
    if (s === "completed") return "avatar-completed";
    if (s === "failed" || s === "declined") return "avatar-failed";
    return "";
  }

  maskPhone(phone: string): string {
    if (!phone) return "";
    // Keep country code and last 3 digits
    const cleaned = phone.replace(/\s/g, "");
    if (cleaned.length <= 6) return phone;
    const countryCode = cleaned.slice(0, cleaned.startsWith("+") ? 3 : 2);
    const lastDigits = cleaned.slice(-3);
    return `${countryCode} ••• ••• ${lastDigits}`;
  }

  liveDuration(call: any): string {
    // Access durationTick to make this reactive
    const _tick = this.durationTick;
    if (typeof _tick === "undefined") return "0:00";

    if (!call.updated_at) return "0:00";
    const start = new Date(call.updated_at).getTime();
    const now = Date.now();
    const seconds = Math.floor((now - start) / 1000);

    if (seconds < 0) return "0:00";

    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }

  get viewLabel(): string {
    const key = this.$t("sol.dialog.view") as string;
    if (key && key !== "sol.dialog.view" && typeof key === "string") {
      return key;
    }
    return "View";
  }

  relativeTime(date: string): string {
    if (!date) return "";
    return this.$moment(date).fromNow();
  }

  async viewCall(call: any): Promise<void> {
    try {
      const callCountry = call.country;
      const currentCountry = this.country;

      // If call is from a different country, navigate there first
      if (callCountry && callCountry !== currentCountry) {
        this.close();
        // Navigate to the correct country, then open the flyout
        await this.$router.push(`/persons/${callCountry}`);
        // Wait for route change to complete
        await this.$nextTick();
        // Small delay to let the page load
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      const person = await this.loadPerson(call);
      if (!person) {
        return;
      }
      this.$store.commit("setPersonSelected", person);
      this.$store.commit("setShouldOpenFlyout", true);
      if (callCountry === currentCountry) {
        this.close();
      }
      window.setTimeout(() => {
        this.scrollToTarget(call);
      }, 500);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to open live call", err);
      this.$toast?.error?.("Unable to open live call");
    }
  }

  private async loadPerson(call: any): Promise<any | null> {
    const personId = call.person_id || call.personId;
    if (!personId) {
      return null;
    }
    // Use the call's country if available, otherwise fall back to current route
    const callCountry = call.country || this.country;
    const owner = this.$store.state.currentOwner;
    const url = `persons/${callCountry}/${personId}${
      owner ? `?owner=${owner}` : ""
    }`;
    return await this.$axios.$get(url);
  }

  private scrollToTarget(call: any): void {
    const targetId =
      call.target_id ||
      call.targetId ||
      this.buildLiveCallAnchor(call.report_name, call);
    if (!targetId || typeof document === "undefined") {
      return;
    }
    const candidateIds: string[] = [];
    if (targetId) {
      candidateIds.push(targetId);
      if (targetId.startsWith("live-call-screening-")) {
        candidateIds.push("live-call-screening-details");
      }
      if (targetId.startsWith("live-call-reference-")) {
        candidateIds.push("live-call-reference-details");
      }
    }
    candidateIds.push(
      "live-call-screening-details",
      "live-call-reference-details"
    );

    let target: HTMLElement | null = null;
    for (const id of candidateIds) {
      if (!id) continue;
      const el = document.getElementById(id);
      if (el) {
        target = el as HTMLElement;
        break;
      }
    }

    if (!target) {
      return;
    }

    const detailsEl =
      target.closest("details") || target.querySelector("details");
    if (detailsEl && !detailsEl.open) {
      detailsEl.open = true;
    }

    const scroll = (): void => {
      if (typeof target.scrollIntoView === "function") {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
      if (
        typeof window !== "undefined" &&
        typeof window.scrollBy === "function"
      ) {
        window.scrollBy({ top: 300, behavior: "smooth" });
      }
    };

    if (
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function"
    ) {
      window.requestAnimationFrame(() => window.requestAnimationFrame(scroll));
    } else {
      setTimeout(scroll, 50);
    }
  }

  private buildLiveCallAnchor(
    reportName: string,
    entry: any
  ): string | undefined {
    if (!entry) {
      return undefined;
    }
    const basePrefix =
      reportName === "candidate_screening"
        ? "live-call-screening"
        : reportName === "reference_check"
        ? "live-call-reference"
        : `live-call-${reportName}`;

    const primary =
      entry.reference_id ||
      entry.room_name ||
      entry.conversation_id ||
      entry.participant_phone;

    if (primary) {
      return `${basePrefix}-${this.sanitizeAnchor(primary)}`;
    }

    if (reportName === "candidate_screening") {
      return "live-call-screening-details";
    }
    if (reportName === "reference_check") {
      return "live-call-reference-details";
    }
    return undefined;
  }

  private sanitizeAnchor(value: string): string {
    return `${value}`.replace(/[^A-Za-z0-9_-]/g, "_");
  }
}
